import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import logo from "../assets/yono-logo.png";

const NAV = [
  { to: "/", label: "Accueil", end: true },
  { to: "/produits", label: "Produits" },
  { to: "/fabrication", label: "Fabrication" },
  { to: "/a-propos", label: "À propos" },
  { to: "/contact", label: "Contact" },
];

export function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const pathname = location.pathname;

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 bg-[var(--yono-off-white)] border-b border-[var(--yono-border)]"
    >
      <div className="container-yono flex items-center justify-between h-[112px]">
        <Link to="/" className="flex items-center" data-testid="logo-link">
          <img src={logo} alt="YONO" className="h-[100px] w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-").replace(/à/g, "a")}`}
              className={({ isActive }) =>
                `nav-link${isActive ? " active" : ""}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex">
          <Link
            to="/gamme"
            className="btn-primary"
            data-testid="header-decouvrir-gamme-btn"
          >
            Découvrir la gamme
          </Link>
        </div>

        <button
          className="lg:hidden p-2"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          className="lg:hidden border-t border-[var(--yono-border)] bg-[var(--yono-off-white)]"
          data-testid="mobile-menu-panel"
        >
          <div className="container-yono py-6 flex flex-col gap-1">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className="py-3 text-[16px] border-b border-[var(--yono-border)]"
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/à/g, "a")}`}
              >
                {n.label}
              </Link>
            ))}
            <Link
              to="/gamme"
              className="btn-primary mt-5 self-start"
              data-testid="mobile-decouvrir-gamme-btn"
            >
              Découvrir la gamme
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

export function StickyCTA() {
  const location = useLocation();
  if (location.pathname.startsWith("/produits") && location.pathname !== "/produits/")
    return null;
  return (
    <Link to="/produits" className="sticky-cta hidden md:inline-flex" data-testid="sticky-cta">
      Découvrir la gamme <ArrowRight size={14} strokeWidth={1.5} />
    </Link>
  );
}

export function Footer() {
  return (
    <footer
      className="bg-[var(--yono-beige)] mt-0 pt-12 pb-16"
      data-testid="site-footer"
    >
      <div className="container-yono grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <img src={logo} alt="YONO" className="h-20 w-auto mb-5" />
          <p className="text-[14px] text-[var(--yono-warm-grey)] max-w-sm leading-relaxed">
            Habitats bois CLT compacts, prêts à raccorder, pour l'hôtellerie de
            plein air, les PRL, les domaines nature et les opérateurs
            d'hébergement professionnel.
          </p>
        </div>
        <div>
          <div className="eyebrow mb-4 mt-4 md:mt-0">Navigation</div>
          <ul className="space-y-3 text-[14px]">
            <li><Link to="/" className="hover:underline">Accueil</Link></li>
            <li><Link to="/produits" className="hover:underline">Produits</Link></li>
            <li><Link to="/a-propos" className="hover:underline">À propos</Link></li>
            <li><Link to="/fabrication" className="hover:underline">Fabrication</Link></li>
            <li><Link to="/contact" className="hover:underline">Contact</Link></li>
            <li className="pt-2"><Link to="/mentions-legales" className="hover:underline">Mentions légales</Link></li>
          </ul>
        </div>
        <div>
          <div className="eyebrow mb-4 mt-4 md:mt-0">Contact</div>
          <ul className="space-y-3 text-[14px] text-[var(--yono-warm-grey)]">
            <li>contact@yono-habitat.com</li>
            <li>+33 (0)6 83 58 18 04</li>
            <li>9 rue Pasteur<br />44310 Saint-Philbert-de-Grand-Lieu</li>
          </ul>
        </div>
      </div>
      <div className="container-yono mt-16 text-[12px] tracking-[0.05em] text-[var(--yono-warm-grey)] flex flex-col md:flex-row justify-between items-start md:items-center gap-2">
        <span>© {new Date().getFullYear()} YONO SAS — Tous droits réservés.</span>
        <span>Site B2B · yono-habitat.com</span>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-[var(--yono-off-white)]">
      <Header />
      <main className="flex-1 pt-[112px]">{children}</main>
      <Footer />
      <StickyCTA />
    </div>
  );
}
